import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { Copy, FileX, FileXls } from 'phosphor-react';
import moment from 'moment';
import 'moment/locale/pt-br';
import { ReturnButton } from '../../../../../components/buttons/ReturnButton';
import { GlassPageBg } from '../../../../../components/glass/GlassPageBg';
import { GlassCard } from '../../../../../components/glass/GlassCard';
import { MonthNavigator } from '../../../../../components/shared/MonthNavigator';
import { useMonthFilter } from '../../../../../hooks/useMonthFilter';
import { useNotasDuplicadas } from '../../../../../hooks/useNotasDuplicadas';
import { useCompanies, ICompany } from '../../../../../hooks/useCompanies';
import { formatCNPJ } from '../../../../../utils/FormatFunctions';
import { GruposDuplicadas } from '../GruposDuplicadas';

moment.locale('pt-br');

const Info = ({
  rotulo,
  valor,
  mono,
}: {
  rotulo: string;
  valor: React.ReactNode;
  mono?: boolean;
}) => (
  <Box minW={0}>
    <Text
      fontSize="10px"
      fontWeight={700}
      color="#94A3B8"
      textTransform="uppercase"
      letterSpacing="0.08em"
    >
      {rotulo}
    </Text>
    <Text
      fontSize="15px"
      fontWeight={700}
      color="#0F172A"
      noOfLines={1}
      fontFamily={mono ? 'mono' : undefined}
    >
      {valor}
    </Text>
  </Box>
);

export function ModulesHomeIntegrationDuplicateNotesShowPage() {
  const { itemId } = useParams<{ itemId: string }>();
  const { getNotasByEmpresa, exportByEmpresa } = useNotasDuplicadas();
  const { getCompanies } = useCompanies();
  const [isExporting, setIsExporting] = useState(false);

  const {
    currentDate: currentFilterDate,
    dateStart,
    dateEnd,
    handlePrevMonth,
    handleNextMonth,
    handleSelectMonth,
  } = useMonthFilter({
    additionalParams: {},
    defaultToPreviousMonth: false,
  });

  const filtersFormMethods = useForm({ mode: 'onChange' });

  // O backend devolve o GRUPO inteiro de quem tem lançamento no mês: o par de
  // uma nota lançada em junho e de novo em julho aparece completo em julho.
  const { data: notasData, isFetching: isFetchingNotas } = useQuery({
    queryKey: [
      'notasDuplicadasEmpresa',
      { codi_emp: itemId, date_start: dateStart, date_end: dateEnd },
    ],
    queryFn: getNotasByEmpresa,
    enabled: !!itemId,
  });

  const { data: empresaData } = useQuery({
    queryKey: ['empresaDetalhes', { external_id: itemId, size: 1 }],
    queryFn: getCompanies,
    enabled: !!itemId,
  });

  const empresa: ICompany | undefined = empresaData?.data?.items?.[0];
  const notas = notasData?.data || [];
  const periodo = useMemo(() => ({ inicio: dateStart, fim: dateEnd }), [dateStart, dateEnd]);

  const handleExport = async () => {
    if (!itemId) return;
    setIsExporting(true);
    try {
      await exportByEmpresa({
        codiEmp: Number(itemId),
        dateStart: dateStart || undefined,
        dateEnd: dateEnd || undefined,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <GlassPageBg>
      <Flex direction="column" gap={5} py={5}>
        <Flex align="center" gap={3} wrap="wrap">
          <ReturnButton previousPage="modulesHome/integration/duplicateNotes" />
          <Flex
            w="46px"
            h="46px"
            borderRadius="14px"
            align="center"
            justify="center"
            flex="none"
            bgGradient="linear(135deg, #F59E0B, #EF4444)"
            color="white"
            boxShadow="0 8px 20px -6px rgba(239,68,68,0.45)"
          >
            <Copy size={24} weight="duotone" />
          </Flex>
          <Box minW={0}>
            <Text
              fontSize="11px"
              letterSpacing="0.14em"
              textTransform="uppercase"
              color="#94A3B8"
              fontWeight={600}
            >
              Integração › Notas Duplicadas
            </Text>
            <Text fontSize="22px" fontWeight={700} letterSpacing="-0.02em" noOfLines={1}>
              {empresa?.razao_social ?? empresa?.nome ?? `Empresa ${itemId}`}
            </Text>
          </Box>
          <Box flex={1} />
          {isFetchingNotas && <Spinner size="sm" color="#F59E0B" />}
          <FormProvider {...filtersFormMethods}>
            <MonthNavigator
              currentDate={currentFilterDate}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
              onSelectMonth={handleSelectMonth}
              compact
            />
          </FormProvider>
          <Button
            size="sm"
            leftIcon={<FileXls size={17} weight="duotone" />}
            variant="outline"
            colorScheme="green"
            borderRadius="10px"
            onClick={handleExport}
            isLoading={isExporting}
            loadingText="Exportando…"
            isDisabled={notas.length === 0}
          >
            Excel
          </Button>
        </Flex>

        <GlassCard intensity="strong" p="16px 20px">
          <Flex gap={8} wrap="wrap">
            <Info rotulo="ID" valor={itemId} mono />
            <Info rotulo="CNPJ" valor={empresa?.cnpj ? formatCNPJ(empresa.cnpj) : '—'} mono />
            <Info rotulo="Mês" valor={currentFilterDate.format('MMMM [de] YYYY')} />
          </Flex>
        </GlassCard>

        {isFetchingNotas && notas.length === 0 && (
          <Flex direction="column" align="center" gap={3} py={12}>
            <Spinner size="lg" color="#F59E0B" thickness="3px" />
            <Text fontSize="13px" color="#64748B">
              Carregando notas…
            </Text>
          </Flex>
        )}
        {!isFetchingNotas && notas.length === 0 && (
          <GlassCard intensity="medium" p={10}>
            <Flex direction="column" align="center" gap={2}>
              <FileX size={40} color="#CBD5E1" />
              <Text fontSize="13px" color="#64748B">
                Nenhuma nota duplicada nesta empresa em {currentFilterDate.format('MMMM [de] YYYY')}
                .
              </Text>
            </Flex>
          </GlassCard>
        )}
        {notas.length > 0 && <GruposDuplicadas notas={notas} periodo={periodo} />}
      </Flex>
    </GlassPageBg>
  );
}
